"use client";

import { Text } from "@/components/atoms/Text/Text";
import { cn } from "@/lib/utils";
import { LayoutGroup, motion, type Transition } from "framer-motion";
import type { OriginFilter as OriginFilterValue } from "../../useArtifactsPage";

interface Props {
  value: OriginFilterValue;
  onChange: (value: OriginFilterValue) => void;
}

interface Option {
  value: OriginFilterValue;
  label: string;
}

const OPTIONS: Option[] = [
  { value: "all", label: "All" },
  { value: "uploaded", label: "Uploaded" },
  { value: "generated", label: "Generated" },
];

const snappySpring: Transition = {
  type: "spring",
  stiffness: 350,
  damping: 30,
  mass: 1,
};

export function OriginFilter({ value, onChange }: Props) {
  return (
    <LayoutGroup id="artifacts-origin-filter" data-sentry-element="LayoutGroup" data-sentry-component="OriginFilter" data-sentry-source-file="OriginFilter.tsx">
      <div className="flex flex-col gap-1">
        <Text variant="small" as="span" tone="muted" className="ml-4" data-sentry-element="Text" data-sentry-source-file="OriginFilter.tsx">
          Type
        </Text>
        <div
          role="tablist"
          aria-label="Filter by type"
          className="flex items-center gap-1"
          data-testid="artifacts-origin-filter"
        >
          {OPTIONS.map((opt) => (
            <OriginTab
              key={opt.value}
              option={opt}
              active={value === opt.value}
              onClick={() => onChange(opt.value)}
            />
          ))}
        </div>
      </div>
    </LayoutGroup>
  );
}

interface OriginTabProps {
  option: Option;
  active: boolean;
  onClick: () => void;
}

function OriginTab({ option, active, onClick }: OriginTabProps) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        "relative rounded-full px-4 py-2 text-body font-medium transition-colors outline-none focus-visible:ring-2 focus-visible:ring-foreground-subtle",
        active
          ? "text-foreground-strong"
          : "text-foreground-tertiary hover:text-foreground-strong",
      )}
      data-testid={`artifacts-origin-filter-${option.value}`}
    data-sentry-component="OriginTab" data-sentry-source-file="OriginFilter.tsx">
      {active ? (
        <motion.span
          layoutId="artifacts-origin-active"
          className="absolute inset-0 rounded-full bg-surface-hover"
          transition={snappySpring}
        />
      ) : null}
      <span className="relative z-10">{option.label}</span>
    </button>
  );
}
